package org.example.project.engineslot

import androidx.compose.runtime.Composable
import org.example.project.dashboard.PopupEngineCard
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

fun slotDriverDisplayName(user: All): String =
    listOf(user.vorname, user.nachname).filter { it.isNotBlank() }.joinToString(" ")
        .ifBlank { user.name.ifBlank { user.email.ifBlank { user.id } } }

fun slotDriverOptionLabel(name: String, tourCount: Int): String =
    "$name | $tourCount ${if (tourCount == 1) "Tour" else "Touren"}"

suspend fun loadSlotDriverScheduleBookings(currentFirmenId: String): List<All> {
    val companyIds = (listOf(currentFirmenId.trim()) + runCatching {
        SlotSnapCompanyApi.loadSelectableCompanies().map { it.key.trim() }
    }.getOrDefault(emptyList()))
        .filter { it.isNotBlank() }
        .distinct()
    return companyIds.flatMap { firmenId ->
        runCatching { loadSlotBookings(firmenId) }.getOrDefault(emptyList())
    }
}

fun slotDriverDayScheduleEntries(bookings: List<All>, driverId: String, date: String): List<String> {
    val normalizedDriverId = driverId.trim()
    val normalizedDate = date.trim()
    if (normalizedDriverId.isBlank() || normalizedDate.isBlank()) return emptyList()
    return bookings
        .filter { booking -> booking.datum.ifBlank { booking.verladedatum }.trim() == normalizedDate }
        .flatMap { booking -> slotDriverScheduleRows(booking, normalizedDriverId) }
        .distinct()
        .sortedBy { it.substringAfterLast(" ") }
}

private fun slotDriverScheduleRows(booking: All, driverId: String): List<String> {
    val artCode = slotBookingArtCode(booking)
    val time = booking.uhrzeitVon.ifBlank { booking.verladeuhrzeit }.ifBlank { booking.uhrzeitAnmeldung }.ifBlank { "--:--" }
    return when (artCode) {
        SLOT_BEREICH_WA -> slotLadenTouren(booking)
            .filter { tour -> (tour.slotDriverUserId.ifBlank { booking.slotDriverUserId }).trim() == driverId }
            .map { tour -> "${tour.lieferscheinnummer.ifBlank { booking.lieferscheinnummer }} Laden $time" }
            .ifEmpty {
                if (booking.slotDriverUserId.trim() == driverId) listOf("${booking.lieferscheinnummer} Laden $time") else emptyList()
            }

        SLOT_BEREICH_WE -> slotLiefernBelege(booking)
            .filter { beleg -> (beleg.slotDriverUserId.ifBlank { booking.slotDriverUserId }).trim() == driverId }
            .map { beleg -> "${beleg.lieferscheinnummer.ifBlank { booking.lieferscheinnummer }} Liefern $time" }
            .ifEmpty {
                if (booking.slotDriverUserId.trim() == driverId) listOf("${booking.lieferscheinnummer} Liefern $time") else emptyList()
            }

        else -> emptyList()
    }
}

@Composable
fun SlotDriverSchedulePopup(entries: List<String>, onClose: () -> Unit) {
    PopupEngineCard(
        windowKey = "slot-driver-schedule-popup",
        widthPx = 520,
        heightPx = 320,
        minWidthPx = 460,
        minHeightPx = 260,
        initialX = 460.0,
        initialY = 180.0,
        resizable = false,
        onClose = onClose
    ) {
        Div({
            style {
                width(100.percent)
                height(100.percent)
                padding(24.px)
                boxSizing("border-box")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(18.px)
                color(Color.white)
            }
        }) {
            Span({
                style {
                    fontSize(18.px)
                    fontWeight("700")
                    textAlign("center")
                }
            }) { Text("Dieser Fahrer hat heute bereits folgende Termine:") }
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(8.px)
                    overflowY("auto")
                    flexGrow(1)
                }
            }) {
                entries.forEach { entry ->
                    Span({
                        style {
                            fontSize(14.px)
                            property("line-height", "1.35")
                        }
                    }) { Text(entry) }
                }
            }
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                }
            }) {
                LumaticaButton(
                    label = "OK",
                    onClick = onClose,
                    variant = LumaticaButtonVariant.Action
                )
            }
        }
    }
}
